/**
 * World map (S16) — the SVG map surface only.
 *
 * One slice of the base-view stylesheet. styles/index.ts concatenates
 * every slice into the single sheet injectStyles mounts — the split is
 * about OWNERSHIP, not about emitting more than one style element.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below
 * is one template literal and a stray backtick terminates it. That has
 * broken the build twice, both times with esbuild pointing at a line ten
 * rows away from the cause. Write class names in plain text.
 *
 * ─── Why the map's paint lives HERE and not at the call site ────────────────
 * world-map-ui.ts builds its map by hand with createElementNS, and it used to
 * paint every node with setAttribute('fill', '#1e293b'). An SVG *presentation
 * attribute* is not CSS: setAttribute('fill', 'var(--brass)') resolves to
 * nothing and the shape renders unpainted — it does not fall back, and nothing
 * logs. So a straight hex-to-token sweep over that module would have silently
 * blanked every zone circle and PoI marker.
 *
 * Two ways out. Rejected: el.style.fill = 'var(--brass)' — it does resolve
 * (the style property IS CSS), but it scatters paint across 20 call sites and
 * leaves the module unable to express "selected" or "tier 3" as anything but
 * a colour decision made inline. Taken: every paint property (fill,
 * fill-opacity, stroke, stroke-width, stroke-dasharray) comes from a class in
 * this file, and world-map-ui.ts only ever decides WHICH class. Geometry
 * (cx/cy/r/points/x/y) stays an attribute, because geometry is per-node data,
 * not style.
 *
 * This makes the map's appearance depend on the injected sheet, which the old
 * attributes did not. Checked before committing to it: the only mount path is
 * startBaseView (base-view/index.ts), which calls injectStyles() at the top of
 * the function, long before createWorldMapUI. A CSS class beats a presentation
 * attribute at any specificity, so this also cannot be half-migrated into a
 * stale attribute winning.
 */
export default `
/* ── World map (S16) ─────────────────────────────────────────────────────── */
.bv-wm-svg {
  display: block;
  background: var(--well);
  border-radius: var(--radius-well);
}

/* Stroke and stroke-width are inherited SVG properties, so setting them on the
   grid group paints every line inside it without per-line rules. */
.bv-wm-grid { stroke: var(--line); stroke-width: 0.5; }

/* Anything clickable. Replaces a per-node style.cursor assignment so the hit
   affordance is declared in one place with the rest of the map's look. */
.bv-wm-hit { cursor: pointer; }

/* ── Zone circles ─────────────────────────────────────────────────────────
   Tier IS risk, so the tiers ride the status trio rather than an arbitrary
   ramp: moss = ok, warn = caution, rust = danger. -tx is the fallback for a
   tier outside 1..3 — without it an unexpected tier would inherit SVG's
   default black fill and read as a hole in the map. */
.bv-wm-zone { stroke: var(--line); stroke-width: 1; fill-opacity: 0.55; }
.bv-wm-zone-t1 { fill: var(--moss); }
.bv-wm-zone-t2 { fill: var(--warn); }
.bv-wm-zone-t3 { fill: var(--rust); }
.bv-wm-zone-tx { fill: var(--bone-faint); }

/* Fogged zones carry no tier class; this rule follows the base one so its
   fill-opacity wins on source order (equal specificity, deliberately — a
   fogged zone is a zone, not a special case that needs to out-rank one). */
.bv-wm-zone-fog { fill: var(--ground); fill-opacity: 0.9; }

/* Dashed edge = the layout engine guessed this blob's position. */
.bv-wm-zone-est { stroke-dasharray: 4 3; }

/* Both are .bv-wm-zone.is-X, so both out-rank the base stroke. is-target is
   listed last on purpose: a region can be selected AND be the live raid's
   target, and the raid marker is the one that must show. */
.bv-wm-zone.is-selected { stroke: var(--brass-bright); stroke-width: 3; }
.bv-wm-zone.is-target { stroke: var(--brass-hot); stroke-width: 3; }

/* ── Zone text ──────────────────────────────────────────────────────────── */
.bv-wm-label { fill: var(--bone); font: 700 13px var(--font-display); letter-spacing: 0.04em; }
.bv-wm-sub { fill: var(--bone-dim); font: 11px var(--font-technical); }
/* --bone-faint is the dimmest text token there is, and the fog's two lines
   need to sit apart from each other — hence fill-opacity for the second rather
   than inventing a token app.css does not declare. */
.bv-wm-fog-label { fill: var(--bone-faint); font: 16px var(--font-note); letter-spacing: 0.06em; }
.bv-wm-fog-tier { fill: var(--bone-faint); fill-opacity: 0.7; font: 11px var(--font-technical); }

/* ── PoI markers ────────────────────────────────────────────────────────────
   The dark stroke is the map ground, not a border colour: it punches the
   marker away from whatever zone fill sits under it. */
.bv-wm-poi { fill: var(--bone-faint); stroke: var(--ground); stroke-width: 1.5; }
.bv-wm-poi.is-found { fill: var(--brass-hot); }

/* ── Live raid overlay ──────────────────────────────────────────────────── */
.bv-wm-raid-ring { fill: none; stroke: var(--brass-hot); stroke-width: 2; stroke-dasharray: 6 4; }
.bv-wm-raid-label { fill: var(--brass-hot); font: 700 12px var(--font-display); letter-spacing: 0.1em; }

/* ── Panel chrome (2026-09-16) ────────────────────────────────────────────
   Everything below used to be an inline style string inside
   world-map-ui.ts — this was the last panel still on the old look, and the
   last one building its own scrim and close cross. The frame itself is now
   the shared one in window.css.ts; what is here is only what the map does
   that no other window does. */
/* Map left, raid options right. Below 720px the options drop under the map. */
.bv-wm-body { display: flex; gap: 12px; align-items: flex-start; }
.bv-wm-body #wm-canvas { flex: 1 1 0; min-width: 0; }
.bv-wm-side { flex: 0 0 250px; min-width: 0; }
.bv-wm-side .bv-wm-focus { flex-direction: column; align-items: stretch; }
.bv-wm-side .bv-wm-focus .bv-act { flex: none; min-height: 44px; width: 100%; }
.bv-wm-side .bv-act { min-height: 44px; }
@media (max-width: 720px) {
  .bv-wm-body { flex-direction: column; }
  .bv-wm-body #wm-canvas, .bv-wm-side { flex: none; width: 100%; }
  .bv-wm-side .bv-wm-focus { flex-direction: row; flex-wrap: wrap; }
  .bv-wm-side .bv-wm-focus .bv-act { flex: 1 1 40%; width: auto; }
}

.bv-wm-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 8px; }
/* The subtitle rides the title's own line here instead of sitting under it,
   because the map's caption is an instruction and the plate below it is the
   whole panel — a second stacked line would push the map down for nothing. */
.bv-wm-head .bv-win-sub { margin: 0; }

.bv-wm-dest {
  margin: 0 0 6px; color: var(--bone);
  font: 0.85rem var(--font-technical); letter-spacing: 0.02em;
}
/* The four raid focuses share the row's width rather than sizing to their
   labels — they are four equal choices, and a row of different-width buttons
   reads as a ranking. */
.bv-wm-focus .bv-act { flex: 1 1 0; min-width: 96px; }

/* ── The launch confirmation ──────────────────────────────────────────────
   Janeks: "Before going to a raid - prompt confirmation + cost of it."
   Takes the four focus buttons' place rather than stacking under them: the
   question has already been asked and the choice is now yes or no. Warn-
   coloured heading for the same reason every window's subtitle is — in this
   game the line that says what you are about to do is always a caution. */
.bv-wm-confirm { padding: 10px 11px; border-color: var(--brass); }
.bv-wm-confirm-ask {
  margin: 0 0 4px; color: var(--brass-hot);
  font: 700 0.95rem var(--font-display); letter-spacing: 0.03em;
  text-transform: uppercase;
}
.bv-wm-confirm-cost {
  margin: 0 0 8px; color: var(--bone-dim);
  font: 0.8rem var(--font-technical); font-variant-numeric: tabular-nums;
}

.bv-wm-active { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.bv-wm-stat {
  flex: 1; color: var(--bone);
  font: 0.85rem var(--font-technical); font-variant-numeric: tabular-nums;
}
.bv-wm-event { flex-basis: 100%; color: var(--bone-dim); font: 0.75rem var(--font-note); }

/* Moss rather than brass: extraction is the get-out-alive action, and the kit
   reserves the green status colour for exactly that. The one button in the
   game that is neither the shared commit nor the shared cancel. */
.bv-wm-extract {
  border-color: var(--moss-dark);
  background:
    radial-gradient(circle at 7px 50%, rgba(220,240,170,0.5) 0 1.5px, rgba(0,0,0,0.55) 2px, transparent 2.6px),
    radial-gradient(circle at calc(100% - 7px) 50%, rgba(220,240,170,0.5) 0 1.5px, rgba(0,0,0,0.55) 2px, transparent 2.6px),
    linear-gradient(180deg, #8a9c58 0%, var(--moss) 45%, var(--moss-dark) 100%);
  color: var(--ground);
}
.bv-wm-extract:hover:not(:disabled) { color: var(--ground); border-color: var(--moss); }

.bv-wm-verdict {
  margin: 0 0 4px;
  font: 700 0.95rem var(--font-display); letter-spacing: 0.06em; text-transform: uppercase;
}
/* CRT green rather than moss for the survived case: moss is a muted olive
   that reads as "fine", and this line has to land as a result readout sitting
   next to a death message. */
.bv-wm-verdict-out { color: var(--terminal); }
.bv-wm-verdict-died { color: var(--rust); }
.bv-wm-result { margin: 0; color: var(--bone); font: 0.85rem/1.5 var(--font-body); }
.bv-wm-revealed { margin: 4px 0 0; color: var(--brass-bright); font: 0.8rem var(--font-note); }
.bv-wm-continue { margin-top: 8px; }
/* Glyph on a PoI marker: dim until the site is found. */
.bv-wm-poi-icon { opacity: 0.5; filter: grayscale(0.6); pointer-events: none; }
.bv-wm-poi-icon.is-found { opacity: 1; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.8)); }

/* ── Bigger map, and the raid screen (2026-10-13) ─────────────────────────
   The map was a 900px window with a 250px column beside it, which left the
   map about 600px wide for what is now twenty-six named zones. The window now
   takes nearly the whole viewport, and the SVG is capped by HEIGHT as well as
   width so a 4:3 map can never grow taller than the screen.

   Raid mode: while a raid runs (or its result waits for Continue) the player is
   out in the wasteland, not in the settlement. The scrim goes fully opaque so
   nothing of the city shows through, and the close cross goes away: the only
   way off this screen is the raid ending. See requestCloseWorldMap. */
#world-map-ui .bv-win { width: min(97vw, 1500px); max-height: 95vh; }
/* The host is sized to the map's own 4:3 so the SVG fills it exactly: a width
   cap by HEIGHT, never a max-height on the SVG, which letterboxes the art with
   black bars. */
#world-map-ui #wm-canvas { width: min(100%, calc((95vh - 150px) * 1.3333)); margin-inline: auto; }
#world-map-ui .bv-wm-svg { width: 100%; height: auto; }
.bv-wm-side { flex-basis: 300px; }
.bv-scrim.bv-wm-raidmode {
  background:
    radial-gradient(120% 90% at 50% 0%, rgba(120,70,28,0.22), transparent 60%),
    repeating-linear-gradient(0deg, rgba(255,255,255,0.012) 0 1px, transparent 1px 3px),
    #0a0705;
}
.bv-wm-raidmode .bv-win-close { display: none; }

/* ── Map stage, floating options plate, grungy buttons (2026-10-13) ───────
   The map and the raid options used to be two columns of one flat window. Now
   they are two separate plates: the map in a framed STAGE, and the options as
   a plate that floats beside it and overlaps the stage's edge, with its own
   shadow so it reads as sitting above the map. The stage keeps a right-hand
   gutter exactly as wide as the overlap, so the plate covers frame and never
   a zone. Under 720px they stack and the plate overlaps the stage's bottom
   edge instead.

   Plate recipe (shared): bevel highlight, dark inner edge, rivets in the
   corners, rust running down from the top, speckle and a scratch. Same family
   as the Survivor Record's plate in paperdoll.css.ts. */
@keyframes bvwm-glint {
  0%, 62% { transform: translateX(-140%) skewX(-18deg); }
  100%    { transform: translateX(300%) skewX(-18deg); }
}
.bv-wm-body { position: relative; gap: 0; align-items: flex-start; }

.bv-wm-stage {
  position: relative; flex: 1 1 0; min-width: 0;
  padding: 14px 70px 14px 14px;
  border: 2px solid #120d08; border-radius: var(--radius-panel);
  background:
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) left 5px top 5px / 10px 10px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) left 5px bottom 5px / 10px 10px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) right 5px top 5px / 10px 10px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) right 5px bottom 5px / 10px 10px no-repeat,
    linear-gradient(180deg, rgba(150,66,26,0.22), transparent) 12% 0 / 24px 120px no-repeat,
    linear-gradient(180deg, rgba(130,56,22,0.18), transparent) 70% 0 / 18px 80px no-repeat,
    linear-gradient(104deg, transparent 47%, rgba(255,235,200,0.05) 47.3%, transparent 48.1%) 0 0 / 360px 240px,
    radial-gradient(circle at 18% 24%, rgba(255,235,200,0.06) 0 1px, transparent 1.5px) 0 0 / 19px 15px,
    radial-gradient(circle at 70% 76%, rgba(0,0,0,0.45) 0 1px, transparent 1.6px) 0 0 / 27px 21px,
    linear-gradient(165deg, #3a2c1c, #1a1410);
  box-shadow: 0 0 0 1px #5a4326, 0 10px 26px rgba(0,0,0,0.6),
              inset 0 2px 0 rgba(255,222,170,0.18), inset 0 -3px 0 rgba(0,0,0,0.55), inset 0 0 40px rgba(0,0,0,0.5);
  touch-action: none; /* the stage owns pinch and drag; see attachMapGestures */
  user-select: none;
}
.bv-wm-stage #wm-canvas {
  border: 2px solid #0c0805; border-radius: var(--radius-well); overflow: hidden;
  box-shadow: 0 0 0 1px #4a3920, inset 0 0 30px rgba(0,0,0,0.7);
}
.bv-wm-stage .bv-wm-svg { background: #0c0805; }

/* The floating plate. */
.bv-wm-side {
  position: relative; z-index: 3; flex: 0 0 300px; margin-left: -56px; margin-top: 34px;
  padding: 18px 16px 16px;
  border: 2px solid #120d08; border-radius: var(--radius-panel);
  background:
    radial-gradient(circle, #f4e0b0 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) left 6px top 6px / 10px 10px no-repeat,
    radial-gradient(circle, #f4e0b0 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) right 6px top 6px / 10px 10px no-repeat,
    radial-gradient(circle, #f4e0b0 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) left 6px bottom 6px / 10px 10px no-repeat,
    radial-gradient(circle, #f4e0b0 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) right 6px bottom 6px / 10px 10px no-repeat,
    repeating-linear-gradient(135deg, #17100a 0 7px, #b8862e 7px 14px) 0 0 / 100% 4px no-repeat,
    linear-gradient(180deg, rgba(150,66,26,0.26), transparent) 82% 4px / 22px 110px no-repeat,
    linear-gradient(104deg, transparent 52%, rgba(255,235,200,0.05) 52.3%, transparent 53%) 0 0 / 260px 180px,
    radial-gradient(circle at 20% 30%, rgba(255,235,200,0.06) 0 1px, transparent 1.5px) 0 0 / 17px 13px,
    linear-gradient(168deg, #46361f, #1e1710);
  box-shadow: 0 0 0 1px #6b5230, 0 22px 44px rgba(0,0,0,0.75), 0 4px 0 rgba(0,0,0,0.5),
              -10px 6px 22px rgba(0,0,0,0.5),
              inset 0 2px 0 rgba(255,222,170,0.25), inset 0 -3px 0 rgba(0,0,0,0.6), inset 0 0 30px rgba(0,0,0,0.4);
}

/* Grungy buttons, scoped to this plate: scratched, rust-streaked steel with a
   stencil face and worn corners. The shared .bv-act stays as it is for every
   other window. The commit buttons keep a brass body, but dirtied; the ghost
   and the extract button get their own metal. */
.bv-wm-side .bv-act {
  position: relative; overflow: hidden; min-height: 46px;
  font: 700 0.85rem var(--font-display); letter-spacing: 0.16em;
  border: 2px solid #0e0a06; border-radius: 3px;
  clip-path: polygon(0 3px, 3px 0, calc(100% - 5px) 0, 100% 4px, 100% calc(100% - 3px), calc(100% - 4px) 100%, 6px 100%, 0 calc(100% - 5px));
  background:
    linear-gradient(180deg, rgba(150,66,26,0.5), transparent 55%) 18% 0 / 14px 100% no-repeat,
    linear-gradient(180deg, rgba(120,48,20,0.45), transparent 70%) 74% 0 / 9px 100% no-repeat,
    linear-gradient(112deg, transparent 38%, rgba(255,235,200,0.14) 38.4%, transparent 39.2%),
    linear-gradient(74deg, transparent 63%, rgba(0,0,0,0.45) 63.3%, transparent 64.1%),
    radial-gradient(circle at 12% 30%, rgba(255,255,255,0.1) 0 1px, transparent 1.6px) 0 0 / 9px 7px,
    radial-gradient(circle at 66% 72%, rgba(0,0,0,0.5) 0 1.2px, transparent 1.8px) 0 0 / 12px 9px,
    radial-gradient(120% 140% at 50% 0%, rgba(255,222,170,0.16), transparent 60%),
    linear-gradient(180deg, #5d4a31 0%, #3a2d1c 50%, #1f160c 100%);
  color: var(--bone);
  text-shadow: 0 1px 0 #000, 0 0 8px rgba(0,0,0,0.6);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.3), inset 0 -3px 0 rgba(0,0,0,0.55),
              inset 0 0 14px rgba(0,0,0,0.5), 0 3px 0 #0a0705, 0 5px 8px rgba(0,0,0,0.6);
  transition: transform 0.12s cubic-bezier(0.3, 1.5, 0.5, 1), filter 0.12s ease, box-shadow 0.12s ease, color 0.12s ease;
}
.bv-wm-side .bv-act-go {
  color: #1a1208; text-shadow: 0 1px 0 rgba(255,236,200,0.45);
  background:
    linear-gradient(180deg, rgba(120,48,20,0.55), transparent 60%) 16% 0 / 13px 100% no-repeat,
    linear-gradient(180deg, rgba(100,40,16,0.5), transparent 75%) 78% 0 / 8px 100% no-repeat,
    linear-gradient(112deg, transparent 38%, rgba(255,246,222,0.3) 38.4%, transparent 39.2%),
    linear-gradient(74deg, transparent 63%, rgba(60,30,6,0.5) 63.3%, transparent 64.1%),
    radial-gradient(circle at 12% 30%, rgba(255,255,255,0.22) 0 1px, transparent 1.6px) 0 0 / 9px 7px,
    radial-gradient(circle at 66% 72%, rgba(70,40,8,0.5) 0 1.2px, transparent 1.8px) 0 0 / 12px 9px,
    linear-gradient(180deg, #d9a752 0%, #b27d32 48%, #7a5320 100%);
  border-color: #3d2a0e;
}
/* A slow light band across each commit button, out of step with its siblings. */
.bv-wm-side .bv-act-go::after {
  content: ""; position: absolute; top: -10%; bottom: -10%; left: 0; width: 30%;
  pointer-events: none; background: linear-gradient(90deg, transparent, rgba(255,246,222,0.34), transparent);
  animation: bvwm-glint 7s ease-in-out infinite;
}
.bv-wm-side .bv-wm-focus .bv-act-go:nth-child(2)::after { animation-delay: -1.8s; }
.bv-wm-side .bv-wm-focus .bv-act-go:nth-child(3)::after { animation-delay: -3.6s; }
.bv-wm-side .bv-wm-focus .bv-act-go:nth-child(4)::after { animation-delay: -5.2s; }
.bv-wm-side .bv-act:hover:not(:disabled) {
  transform: translateY(-2px); filter: brightness(1.18);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.4), inset 0 -3px 0 rgba(0,0,0,0.55), inset 0 0 14px rgba(0,0,0,0.4),
              0 5px 0 #0a0705, 0 9px 14px rgba(0,0,0,0.6), 0 0 14px rgba(255,189,92,0.25);
}
.bv-wm-side .bv-act:active:not(:disabled) {
  transform: translateY(2px); filter: brightness(0.9);
  box-shadow: inset 0 3px 8px rgba(0,0,0,0.7), 0 1px 0 #0a0705;
}
.bv-wm-side .bv-act-ghost {
  color: var(--bone-dim);
  background:
    radial-gradient(circle at 12% 30%, rgba(255,255,255,0.06) 0 1px, transparent 1.6px) 0 0 / 9px 7px,
    linear-gradient(112deg, transparent 44%, rgba(255,235,200,0.1) 44.4%, transparent 45.2%),
    linear-gradient(180deg, #2a2016, #130d08);
}
.bv-wm-side .bv-wm-extract {
  color: #10140a; text-shadow: 0 1px 0 rgba(235,245,190,0.4); border-color: #2e3a14;
  background:
    linear-gradient(180deg, rgba(110,60,24,0.5), transparent 60%) 16% 0 / 13px 100% no-repeat,
    linear-gradient(112deg, transparent 38%, rgba(235,245,190,0.28) 38.4%, transparent 39.2%),
    linear-gradient(74deg, transparent 63%, rgba(20,30,6,0.5) 63.3%, transparent 64.1%),
    radial-gradient(circle at 12% 30%, rgba(255,255,255,0.2) 0 1px, transparent 1.6px) 0 0 / 9px 7px,
    radial-gradient(circle at 66% 72%, rgba(20,30,6,0.5) 0 1.2px, transparent 1.8px) 0 0 / 12px 9px,
    linear-gradient(180deg, #9db062 0%, var(--moss) 48%, var(--moss-dark) 100%);
}
.bv-wm-side .bv-wm-confirm { background: rgba(0,0,0,0.35); box-shadow: inset 0 2px 6px rgba(0,0,0,0.7); border-color: #4a3920; }
.bv-wm-side .bv-wm-dest { text-shadow: 0 1px 0 #000; }

/* Map text: sized for the 80-column grid (the viewBox is twice as wide as it was),
   with a dark halo so a name stays readable over painted ground. */
.bv-wm-label { font-size: 20px; }
.bv-wm-sub { font-size: 14px; }
.bv-wm-fog-label { font: 700 18px var(--font-display); letter-spacing: 0.05em; }
.bv-wm-fog-tier { font-size: 14px; }
.bv-wm-label, .bv-wm-sub, .bv-wm-fog-label, .bv-wm-fog-tier {
  stroke: rgba(10,7,4,0.9); stroke-width: 4px; stroke-linejoin: round; paint-order: stroke;
  pointer-events: none;
}
.bv-wm-zone { stroke-width: 2.5; fill-opacity: 0.8; }
.bv-wm-zone-fog { fill-opacity: 0.55; stroke: var(--bone-faint); stroke-dasharray: 5 4; }
.bv-wm-zone.is-selected { stroke-width: 4; }
.bv-wm-zone.is-target { stroke-width: 4; }
.bv-wm-hit:hover .bv-wm-zone { filter: brightness(1.3); }
.bv-wm-poi { stroke-width: 2; }

@media (max-width: 720px) {
  .bv-wm-body { flex-direction: column; }
  /* Bottom padding is exactly the overlap, so the plate sits over frame and never over map. */
  .bv-wm-stage { flex: none; width: 100%; padding: 10px 10px 40px; }
  /* Stacked: the plate overlaps the stage's bottom edge instead of its side. */
  .bv-wm-side { flex: none; width: calc(100% - 20px); margin: -22px 10px 0; }
}
@media (prefers-reduced-motion: reduce) {
  .bv-wm-side .bv-act-go::after { animation: none; }
  .bv-wm-side .bv-act { transition: none; }
  .bv-wm-pencil-sweep.is-drawing { animation: none; stroke-dashoffset: 0; }
}

/* ── Fog of war, the pencil circle, and no big backing plate (2026-10-13) ──
   Janeks: *"Remove the background of the large section (map AND button
   container). Leave only map container and the buttons container."* The window
   frame around them loses its plate, border, shadow and dirt overlay; the two
   plates inside it are all that is drawn. The title and the close cross stay,
   on the scrim. */
#world-map-ui .bv-win {
  background: none; border: 0; box-shadow: none; border-radius: 0;
}
#world-map-ui .bv-win::before, #world-map-ui .bv-win::after { display: none; }

/* The fog: a near-black veil over the painting, holes cut by the mask. The mask
   gradient is black-to-transparent so overlapping holes merge. */
.bv-wm-fog { fill: var(--ground); fill-opacity: 0.94; pointer-events: none; }
.bv-wm-fog-white { fill: #fff; }
.bv-wm-fog-hole { fill: url(#wm-fog-hole); }
.bv-wm-fog-in { stop-color: #000; stop-opacity: 1; }
.bv-wm-fog-out { stop-color: #000; stop-opacity: 0; }

/* The pencil circle. Drawn by sweeping a mask round the loop: the stroke is
   pathLength 1, so dasharray 1 and an offset from 1 to 0 draws it end to end. */
.bv-wm-pencil, .bv-wm-pencil-img { pointer-events: none; }
.bv-wm-pencil-img { filter: drop-shadow(0 1px 1.5px rgba(0,0,0,0.55)); }
.bv-wm-pencil-sweep { fill: none; stroke: #fff; stroke-dasharray: 1; stroke-dashoffset: 0; }
.bv-wm-pencil-sweep.is-drawing { stroke-dashoffset: 1; animation: bvwm-pencil-draw 0.45s cubic-bezier(0.3, 0.1, 0.4, 1) forwards; }
@keyframes bvwm-pencil-draw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }

/* The pencil replaces the flat ring: a selected zone keeps its normal outline. */
.bv-wm-zone.is-selected { stroke: var(--line); stroke-width: 2.5; }

/* ── The map container is exactly the map plus its padding (2026-10-13) ───
   Janeks: *"Make the Map container the size of the map, not larger."* On
   desktop the window shrinks to its content, the stage is the map's own width
   (a 4:3 picture sized by viewport height, and by width so the plate still
   fits) plus its padding, and the map fills it. The right-hand padding is the
   gutter the options plate overlaps, so the plate covers frame, never map.
   Phones keep the stacked, full-width layout above. */
@media (min-width: 721px) {
  #world-map-ui { --wm-w: min(calc((95vh - 150px) * 1.3333), calc(100vw - 440px)); }
  #world-map-ui .bv-win { width: auto; max-width: 97vw; }
  .bv-wm-stage { flex: none; width: calc(var(--wm-w) + 46px); padding: 14px 28px 14px 14px; }
  #world-map-ui #wm-canvas { width: 100%; }
  .bv-wm-side { margin-left: -28px; }
}

`;
